import { USER_LOAN_PROGRAM } from '@/config/constants';
import { HOMEOWNERS_INSURANCE_RATE } from '@/utils/calculator/assumptions';
import { estimateStateClosingTaxes } from '@/utils/fees/stateClosingTaxes';
import { CalculateClosingDisclosureProps, calculateTaxFactor } from '.';

/**
 * Cash purchase estimate: 0 interest, no loan, no lender fees.
 * Cash to close = purchase price + buyer-side title/gov/prepaid costs − credits.
 */
export const calculateCashClosingDisclosure = ({
  offerPrice,
  listPrice,
  downPayment: _downPayment,
  zipCode = 0,
  hoaFees = 0,
  commission = 0,
  monthlyPropertyTaxes: monthlyPropertyTaxesOverride,
  monthlyHomeownersInsurance: monthlyHomeownersInsuranceOverride,
  marketInterestRate = 0,
  creditScore = 0,
  loanTerm = 0,
}: CalculateClosingDisclosureProps) => {
  const purchasePrice = offerPrice || listPrice;
  const downPayment = purchasePrice;
  const loanAmount = 0;
  const interestRate = 0;
  const ltv = 0;

  const taxFactor = calculateTaxFactor(zipCode);

  const titleTitleSearchBase = 250;
  const recordingDeedBase = 75;
  const optionalOwnersTitleInsurance = purchasePrice * 0.0035;
  const titleSettlementAgentFee = 650;
  const insuranceBinder = 25;
  const surveyFee = 450;
  const pestInspectionFee = 0;
  const earnestMoneyDeposit = 0;
  const lenderCredits = 0;

  // taxFactor is already the ZIP's annual effective rate in percent — same
  // formula as every other calculator. (Was price × 1.25% × taxFactor, so
  // switching a property to Cash raised its taxes by 25%.)
  const annualPropertyTax = (purchasePrice * taxFactor) / 100;
  const monthlyPropertyTaxesBase = annualPropertyTax / 12;
  const monthlyPropertyTaxes =
    monthlyPropertyTaxesOverride ?? monthlyPropertyTaxesBase;
  const monthlyHomeownersInsuranceBase =
    (purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12;
  const monthlyHomeownersInsurance =
    monthlyHomeownersInsuranceOverride ?? monthlyHomeownersInsuranceBase;

  const upfrontHomeownersInsurance = purchasePrice * HOMEOWNERS_INSURANCE_RATE;
  const prepaidsHomeownersInsurance = monthlyHomeownersInsurance * 3;
  const prepaidsPropertyTaxes = monthlyPropertyTaxes * 3;
  const titleOwnersTitleInsuranceBase = purchasePrice * 0.0045;
  const homeInspectionFeeBase = purchasePrice * 0.0035;
  const homeInspectionFee = Math.min(
    Math.max(homeInspectionFeeBase, 650),
    4500
  );

  // Buyer's share of the state deed/transfer tax (by state custom) and the
  // borrower's loan taxes — see utils/fees/stateClosingTaxes.
  const stateTaxes = estimateStateClosingTaxes({
    zipCode,
    price: purchasePrice,
    loanAmount,
  });
  const transferTaxes = stateTaxes.transferTaxes;
  const mortgageTaxes = stateTaxes.mortgageTaxes;
  const titleCosts =
    optionalOwnersTitleInsurance +
    titleTitleSearchBase +
    titleOwnersTitleInsuranceBase +
    surveyFee +
    titleSettlementAgentFee +
    insuranceBinder;
  const govCosts = recordingDeedBase + transferTaxes + mortgageTaxes;
  const prepaidCosts = prepaidsPropertyTaxes + prepaidsHomeownersInsurance;
  const lenderCostTotals = 0;
  const closingCosts = titleCosts + govCosts + prepaidCosts;
  const cashToClose = closingCosts + downPayment - earnestMoneyDeposit;
  const cashOutsideClosing = earnestMoneyDeposit + homeInspectionFee;
  const unbeatableCredit = (purchasePrice * commission) / 100;
  const unbeatableDeal = cashToClose - unbeatableCredit;

  return {
    type: USER_LOAN_PROGRAM.CASH,
    offerPrice,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate: 0,
    creditScore,
    downPayment,
    loanAmount,
    interestRate,
    ltv,
    upfrontHomeownersInsurance,
    cleanPayment: monthlyPropertyTaxes + monthlyHomeownersInsurance + hoaFees,
    cashOutsideClosing,
    monthlyPMIPayment: 0,
    processingFeeBase: 0,
    principalAndInterest: 0,
    homeInspectionFee,
    loanTerm,
    optionalOwnersTitleInsurance,
    hoaFees,
    earnestMoneyDeposit,
    piti: monthlyPropertyTaxes + monthlyHomeownersInsurance + hoaFees,
    estimatedEscrow: monthlyPropertyTaxes + monthlyHomeownersInsurance,
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    prepaidsHomeownersInsurance,
    prepaidsPropertyTaxes,
    prepaidInterest: 0,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase: 0,
    titleTitleSearchBase,
    insuranceBinder,
    recordingDeedBase,
    appraisalFeeBase: 0,
    underwritingFee: 0,
    applicationFeeBase: 0,
    pestInspectionFee,
    floodMonitoringFee: 0,
    floodDeterminationFee: 0,
    creditReportFeeBase: 0,
    recordingTitle: 0,
    recordingMortgage: 0,
    countyRecordingFees: 0,
    stateRecordingFees: 0,
    surveyFee,
    initialEscrowTaxes: 0,
    initialEscrowMortgageInsurance: 0,
    initialEscrowHOI: 0,
    lenderCredits,
    lenderCostTotals,
    titleCosts,
    govCosts,
    prepaidCosts,
    closingCosts,
    // Exactly what closingCosts sums — the fee breakdown itemizes these.
    closingCostParts: {
      optionalOwnersTitleInsurance,
      titleTitleSearchBase,
      titleOwnersTitleInsuranceBase,
      surveyFee,
      titleSettlementAgentFee,
      insuranceBinder,
      recordingDeedBase,
      transferTaxes,
      mortgageTaxes,
      prepaidsPropertyTaxes,
      prepaidsHomeownersInsurance,
    },
    cashToClose,
    unbeatableDeal,
    transferTaxes,
    mortgageTaxes,
    stateTaxLines: stateTaxes.lines,
    stateTaxState: stateTaxes.state,
    stateTaxNote: stateTaxes.localNote,
    removePrepaymentPenalty: false,
    prepaymentPenaltyYear1: 0,
    prepaymentPenaltyYear2: 0,
    prepaymentPenaltyYear3: 0,
    prepaymentPenaltyYear4: 0,
    prepaymentPenaltyYear5: 0,
    additionalLenderOriginationFee: 0,
  };
};

export type ProductCashEsitmation = ReturnType<
  typeof calculateCashClosingDisclosure
>;
